import { useQuery } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import { Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { useTenant } from "hooks/useTenant";
import { useMemo, useState } from "react";

import { FACILITIES_QUERY } from "./AssociateFacilitiesDialog";

type Props = {
  facilityIds: string[];
  noRowsMessage?: string;
  onDelete: (id: string) => void;
  includeTenantFilter?: boolean;
  facilityIdsLoading?: boolean;
  showTenantColumn?: boolean;
};

export const AssociatedFacilityGrid = ({
  facilityIds,
  noRowsMessage,
  onDelete,
  includeTenantFilter = true,
  facilityIdsLoading = false,
  showTenantColumn = false,
}: Props) => {
  const { tenantId, loading: isTenantLoading } = useTenant();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });
  const { data, previousData, loading } = useQuery(FACILITIES_QUERY, {
    variables: {
      search: (includeTenantFilter ? `tenantId:${tenantId}` : "") + ` id:${facilityIds.join("|")}`,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sort: [
        { field: "tenant.name", sort: "asc" },
        { field: "name", sort: "asc" },
      ],
    },
    skip: isTenantLoading || facilityIds.length === 0,
  });

  const items = useMemo(
    () => data?.facilities.items ?? previousData?.facilities.items ?? [],
    [data?.facilities.items, previousData?.facilities.items]
  );

  const columns = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "name",
        headerName: "Facility Name",
        flex: 1,
        valueGetter: (_, row) => row.name,
        renderCell: ({ value }) => {
          return (
            <Tooltip title={value}>
              <Typography
                variant="body2"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {value}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "streetAddress1",
        headerName: "Location",
        flex: 1,
        sortable: false,
        valueGetter(_, row) {
          if (!row.streetAddress1) return "";

          const { streetAddress1, streetAddress2, city, state, zip } = row;

          return [`${streetAddress1} ${streetAddress2}`.trim(), city, state, zip]
            .filter((s) => s)
            .join(", ");
        },
        renderCell: ({ value }) => {
          return (
            <Tooltip title={value}>
              <Typography
                variant="body2"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {value}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        flex: 0.1,
        getActions: (params) => {
          return [
            <Tooltip title="Delete facility association" key={1}>
              <GridActionsCellItem
                icon={<Delete />}
                label="Delete"
                onClick={() => {
                  onDelete(params.row.id);
                }}
              />
            </Tooltip>,
          ];
        },
      },
    ];

    if (!showTenantColumn) {
      return baseColumns;
    }

    return [
      {
        field: "tenant",
        headerName: "Tenant",
        flex: 1,
        valueGetter: (_, row) => row.tenant.name,
      },
      ...baseColumns,
    ];
  }, [showTenantColumn, onDelete]);

  return (
    <div>
      <DataGrid
        noRowsMessage={noRowsMessage ?? "No facilities are currently associated with this."}
        rows={items}
        getRowId={(row) => row.id}
        columns={columns}
        paginationMode="server"
        pagination
        paginationModel={paginationModel}
        pageSizeOptions={[5, 10, 25, 50]}
        onPaginationModelChange={(model) => {
          setPaginationModel(model);
        }}
        rowCount={data?.facilities.count ?? 0}
        loading={loading || facilityIdsLoading}
        sx={{
          maxHeight: "300px",
          "& .MuiDataGrid-virtualScroller": {
            minHeight: items.length === 0 ? "100px" : "0",
          },
        }}
      />
    </div>
  );
};
